import React from 'react'
import { observer } from 'mobx-react-lite'

import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Stack from '@mui/material/Stack'

import { InstitutionLogo, P, Text } from '@mxenabled/mxui'

import { useAppConfig, useGlobalCopyStore, useGlobalUiStore } from '../../common'
import { type Account, AccountType } from '../../core'
import getAccountDisplayName from '../../common/utils/AccountDisplayName'

import { getAccountBalanceDetails } from '../utils/AccountFields'
import { formatCurrency } from '../../core/utils/NumberFormatting'

interface AccountsListCompactProps {
  onClick?: (account: Account) => void
  shouldShowBalance?: boolean
  loadAccounts?: AccountType[]
}

const AccountsListCompact = ({
  onClick,
  shouldShowBalance = true,
  loadAccounts,
}: AccountsListCompactProps) => {
  const { config } = useAppConfig()
  const { accounts: accountsData } = useGlobalCopyStore()
  const { selectedAccounts } = useGlobalUiStore()

  const handleAccountClick = React.useCallback(
    (account: Account) => {
      onClick?.(account)
    },
    [onClick],
  )

  const uniqueAccountTypes = React.useMemo(
    () =>
      Array.from(new Set(selectedAccounts.map((account) => account.account_type))).filter(
        (accountType) => (loadAccounts ? loadAccounts.includes(accountType) : true),
      ),
    [selectedAccounts, loadAccounts],
  )

  if (!selectedAccounts.length) return null

  return (
    <React.Fragment>
      {uniqueAccountTypes.map((accountType) => {
        const { accountsForType, totalBalanceForType } = selectedAccounts.reduce(
          (acc, account) => {
            if (account.account_type === accountType) {
              acc.accountsForType.push(account)
              acc.totalBalanceForType += account.balance || 0
            }

            return acc
          },
          { accountsForType: [] as Account[], totalBalanceForType: 0 },
        )

        return (
          <List
            key={accountType}
            subheader={
              <ListSubheader sx={{ pb: 4, position: 'static', pt: 12, px: 24 }}>
                <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Text bold={true} variant="caption">
                    {accountsData.account_types[accountType]}
                  </Text>
                  {shouldShowBalance && (
                    <Text bold={true} variant="caption">
                      {formatCurrency(totalBalanceForType, '0,0.00')}
                    </Text>
                  )}
                </Stack>
              </ListSubheader>
            }
            sx={{ bgcolor: 'background.paper' }}
          >
            {accountsForType.map((account, index) => {
              const { firstValue: accountBalance } = getAccountBalanceDetails(
                account,
                accountsData,
                config.display_available_balance_in_accounts,
              )
              const displayName = getAccountDisplayName(account, {
                allowNickname: config?.show_account_nicknames_in_master,
              })

              const listItemContent = (
                <>
                  <ListItemAvatar>
                    <InstitutionLogo alt="MX" institutionGuid={account.institution_guid ?? ''} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${displayName}${account.account_number ? ` *${account.account_number}` : ''}`}
                    slotProps={{ primary: { noWrap: true, pr: 4, variant: 'body1' } }}
                  />
                  {shouldShowBalance && (
                    <P bold={true} variant="body2">
                      {formatCurrency(accountBalance ?? 0, '0,0.00')}
                    </P>
                  )}
                </>
              )

              return (
                <React.Fragment key={account.guid}>
                  <ListItem sx={{ px: onClick ? 0 : 24 }}>
                    {onClick ? (
                      <ListItemButton onClick={() => handleAccountClick(account)}>
                        {listItemContent}
                      </ListItemButton>
                    ) : (
                      listItemContent
                    )}
                  </ListItem>
                  {index !== accountsForType.length - 1 && <Divider variant="inset" />}
                </React.Fragment>
              )
            })}
            <Divider />
          </List>
        )
      })}
    </React.Fragment>
  )
}

export default observer(AccountsListCompact)
